import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Upload, Trash2, Play, Settings, Monitor, Plus,
  GripVertical, Star, Pencil, Check, X, MousePointer, Link,
  Home, ChevronLeft, ChevronRight, Maximize2, Image as ImageIcon,
  FileText, Volume2, VolumeX, Info
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// ==================== ADMIN MODE ====================

const ScreenCard = ({ screen, screens, onDelete, onUpdate, onSetStart, onEditHotspots, onReplaceMedia }) => {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(screen.name);
  const [editingDesc, setEditingDesc] = useState(false);
  const [description, setDescription] = useState(screen.description || '');
  const fileInputRef = useRef(null);

  const handleSave = () => {
    onUpdate(screen.id, { name });
    setEditing(false);
  };

  const handleSaveDesc = () => {
    onUpdate(screen.id, { description });
    setEditingDesc(false);
  };

  const isVideo = screen.content_type?.startsWith('video') || screen.content_type === 'image/gif';

  return (
    <div className="bg-white/5 border border-white/10 rounded-lg overflow-hidden group" data-testid={`screen-card-${screen.id}`}>
      <div className="relative aspect-video bg-black/50 flex items-center justify-center overflow-hidden">
        {isVideo ? (
          <video
            src={`${API}/envivom/media/${screen.file_id}`}
            className="w-full h-full object-contain"
            autoPlay loop muted playsInline
          />
        ) : (
          <img
            src={`${API}/envivom/media/${screen.file_id}`}
            alt={screen.name}
            className="w-full h-full object-contain"
          />
        )}
        {screen.is_start_screen && (
          <div className="absolute top-2 left-2 bg-amber-500 text-black text-[10px] font-bold px-2 py-0.5 rounded">
            START
          </div>
        )}
        <div className="absolute top-2 right-2 flex gap-1">
          <span className="bg-black/60 text-gray-300 text-[10px] px-2 py-0.5 rounded">
            {screen.hotspots?.length || 0} hotspots
          </span>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept="image/*,video/mp4,video/quicktime,video/webm,.gif"
          onChange={async (e) => {
            if (e.target.files[0]) {
              await onReplaceMedia(screen.id, e.target.files[0]);
              e.target.value = '';
            }
          }}
        />
      </div>
      <div className="p-3">
        {editing ? (
          <div className="flex gap-2">
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              className="flex-1 bg-white/10 border border-white/20 rounded px-2 py-1 text-sm text-white"
              autoFocus
              onKeyDown={e => e.key === 'Enter' && handleSave()}
            />
            <button onClick={handleSave} className="text-green-400 hover:text-green-300"><Check className="w-4 h-4" /></button>
            <button onClick={() => { setEditing(false); setName(screen.name); }} className="text-gray-400 hover:text-gray-300"><X className="w-4 h-4" /></button>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <span className="text-white text-sm font-medium truncate">{screen.name}</span>
            <button onClick={() => setEditing(true)} className="text-gray-500 hover:text-gray-300 ml-2"><Pencil className="w-3 h-3" /></button>
          </div>
        )}

        {/* Description editor */}
        <div className="mt-2">
          {editingDesc ? (
            <div className="space-y-1.5">
              <textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                className="w-full bg-white/10 border border-white/20 rounded px-2 py-1.5 text-xs text-white resize-none"
                rows={3}
                placeholder="Describe this screen for the demo viewer..."
                autoFocus
              />
              <div className="flex gap-1.5 justify-end">
                <button onClick={handleSaveDesc} className="text-green-400 hover:text-green-300 text-xs flex items-center gap-1">
                  <Check className="w-3 h-3" /> Save
                </button>
                <button onClick={() => { setEditingDesc(false); setDescription(screen.description || ''); }} className="text-gray-500 hover:text-gray-300 text-xs">
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setEditingDesc(true)}
              className="w-full text-left mt-1"
              data-testid={`edit-desc-${screen.id}`}
            >
              {screen.description ? (
                <p className="text-gray-400 text-[11px] line-clamp-2 hover:text-gray-300">{screen.description}</p>
              ) : (
                <p className="text-gray-600 text-[11px] italic hover:text-gray-400 flex items-center gap-1">
                  <FileText className="w-3 h-3" /> Add description...
                </p>
              )}
            </button>
          )}
        </div>

        <div className="flex gap-1.5 mt-3">
          <button
            onClick={() => onEditHotspots(screen)}
            className="flex-1 bg-blue-600/20 hover:bg-blue-600/40 text-blue-400 text-xs py-1.5 rounded flex items-center justify-center gap-1"
            data-testid={`edit-hotspots-${screen.id}`}
          >
            <MousePointer className="w-3 h-3" /> Hotspots
          </button>
          {!screen.is_start_screen && (
            <button
              onClick={() => onSetStart(screen.id)}
              className="bg-amber-600/20 hover:bg-amber-600/40 text-amber-400 text-xs py-1.5 px-2 rounded"
              title="Set as start screen"
            >
              <Star className="w-3 h-3" />
            </button>
          )}
          <button
            onClick={() => fileInputRef.current?.click()}
            className="bg-white/5 hover:bg-white/10 text-gray-400 text-xs py-1.5 px-2 rounded"
            title="Replace media"
          >
            <ImageIcon className="w-3 h-3" />
          </button>
          <button
            onClick={() => onDelete(screen.id)}
            className="bg-red-600/20 hover:bg-red-600/40 text-red-400 text-xs py-1.5 px-2 rounded"
            data-testid={`delete-screen-${screen.id}`}
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  );
};

// ==================== HOTSPOT EDITOR ====================

const HotspotEditor = ({ screen, screens, onSave, onClose }) => {
  const [hotspots, setHotspots] = useState(screen.hotspots || []);
  const [drawing, setDrawing] = useState(false);
  const [drawStart, setDrawStart] = useState(null);
  const [currentRect, setCurrentRect] = useState(null);
  const [selectedHotspot, setSelectedHotspot] = useState(null);
  const [linking, setLinking] = useState(null);
  const [editingTooltip, setEditingTooltip] = useState(null);
  const containerRef = useRef(null);

  const isVideo = screen.content_type?.startsWith('video') || screen.content_type === 'image/gif';

  const getRelativePos = (e) => {
    const rect = containerRef.current.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) / rect.width) * 100,
      y: ((e.clientY - rect.top) / rect.height) * 100
    };
  };

  const handleMouseDown = (e) => {
    if (linking || editingTooltip) return;
    if (e.target.closest('[data-hotspot-control]')) return;
    const pos = getRelativePos(e);
    setDrawing(true);
    setDrawStart(pos);
    setCurrentRect({ x: pos.x, y: pos.y, width: 0, height: 0 });
    setSelectedHotspot(null);
  };

  const handleMouseMove = (e) => {
    if (!drawing || !drawStart) return;
    const pos = getRelativePos(e);
    setCurrentRect({
      x: Math.min(drawStart.x, pos.x),
      y: Math.min(drawStart.y, pos.y),
      width: Math.abs(pos.x - drawStart.x),
      height: Math.abs(pos.y - drawStart.y)
    });
  };

  const handleMouseUp = () => {
    if (!drawing || !currentRect) { setDrawing(false); return; }
    setDrawing(false);
    if (currentRect.width > 1 && currentRect.height > 1) {
      const newHotspot = {
        id: `hs-${Date.now()}`,
        x: currentRect.x,
        y: currentRect.y,
        width: currentRect.width,
        height: currentRect.height,
        target_screen_id: '',
        label: '',
        tooltip: ''
      };
      setHotspots(prev => [...prev, newHotspot]);
      setSelectedHotspot(newHotspot.id);
      setLinking(newHotspot.id);
    }
    setCurrentRect(null);
    setDrawStart(null);
  };

  const deleteHotspot = (id) => {
    setHotspots(prev => prev.filter(h => h.id !== id));
    if (selectedHotspot === id) setSelectedHotspot(null);
    if (linking === id) setLinking(null);
    if (editingTooltip === id) setEditingTooltip(null);
  };

  const linkHotspot = (hotspotId, targetScreenId) => {
    setHotspots(prev => prev.map(h => h.id === hotspotId ? { ...h, target_screen_id: targetScreenId } : h));
    setLinking(null);
  };

  const updateTooltip = (hotspotId, tooltip) => {
    setHotspots(prev => prev.map(h => h.id === hotspotId ? { ...h, tooltip } : h));
  };

  const handleSave = () => {
    onSave(screen.id, hotspots);
  };

  const otherScreens = screens.filter(s => s.id !== screen.id);

  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex flex-col" data-testid="hotspot-editor">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-900 border-b border-white/10">
        <div className="flex items-center gap-3">
          <h3 className="text-white font-medium">Editing Hotspots: {screen.name}</h3>
          <span className="text-gray-500 text-xs">{hotspots.length} hotspot(s) — Draw rectangles, then link & add tooltips</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleSave}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-1.5 rounded text-sm font-medium"
            data-testid="save-hotspots-btn"
          >
            Save
          </button>
          <button onClick={onClose} className="text-gray-400 hover:text-white px-3 py-1.5 text-sm">Cancel</button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Image area */}
        <div className="flex-1 flex items-center justify-center p-4 overflow-auto">
          <div
            ref={containerRef}
            className="relative select-none max-w-full max-h-full"
            style={{ cursor: linking || editingTooltip ? 'default' : 'crosshair' }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
          >
            {isVideo ? (
              <video
                src={`${API}/envivom/media/${screen.file_id}`}
                className="max-w-full max-h-[calc(100vh-120px)] object-contain rounded pointer-events-none"
                autoPlay loop muted playsInline draggable={false}
              />
            ) : (
              <img
                src={`${API}/envivom/media/${screen.file_id}`}
                alt={screen.name}
                className="max-w-full max-h-[calc(100vh-120px)] object-contain rounded"
                draggable={false}
              />
            )}

            {/* Existing hotspots */}
            {hotspots.map(hs => {
              const targetScreen = screens.find(s => s.id === hs.target_screen_id);
              return (
                <div
                  key={hs.id}
                  data-hotspot-control="true"
                  className={`absolute border-2 rounded transition-colors ${
                    selectedHotspot === hs.id ? 'border-blue-400 bg-blue-500/25' :
                    hs.target_screen_id ? 'border-green-400/70 bg-green-500/15' :
                    'border-amber-400/70 bg-amber-500/15'
                  }`}
                  style={{
                    left: `${hs.x}%`, top: `${hs.y}%`,
                    width: `${hs.width}%`, height: `${hs.height}%`
                  }}
                  onClick={(e) => { e.stopPropagation(); setSelectedHotspot(hs.id); }}
                >
                  <div className="absolute -top-5 left-0 flex gap-1 text-[10px]" data-hotspot-control="true">
                    {targetScreen ? (
                      <span className="bg-green-600/80 text-white px-1 rounded">{targetScreen.name}</span>
                    ) : (
                      <span className="bg-amber-600/80 text-white px-1 rounded">unlinked</span>
                    )}
                    <button
                      onClick={(e) => { e.stopPropagation(); setLinking(hs.id); setEditingTooltip(null); }}
                      className="bg-blue-600/80 text-white px-1 rounded hover:bg-blue-500"
                      title="Link to screen"
                    >
                      <Link className="w-3 h-3 inline" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setEditingTooltip(hs.id); setLinking(null); }}
                      className={`${hs.tooltip ? 'bg-cyan-600/80' : 'bg-gray-600/80'} text-white px-1 rounded hover:bg-cyan-500`}
                      title="Edit tooltip"
                    >
                      <Info className="w-3 h-3 inline" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); deleteHotspot(hs.id); }}
                      className="bg-red-600/80 text-white px-1 rounded hover:bg-red-500"
                    >
                      <X className="w-3 h-3 inline" />
                    </button>
                  </div>
                  {/* Show tooltip indicator */}
                  {hs.tooltip && (
                    <div className="absolute -bottom-4 left-0 text-[9px] text-cyan-400/80 truncate max-w-full" data-hotspot-control="true">
                      {hs.tooltip.substring(0, 30)}{hs.tooltip.length > 30 ? '...' : ''}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Drawing rect */}
            {currentRect && (
              <div
                className="absolute border-2 border-blue-400 bg-blue-500/20 rounded pointer-events-none"
                style={{
                  left: `${currentRect.x}%`, top: `${currentRect.y}%`,
                  width: `${currentRect.width}%`, height: `${currentRect.height}%`
                }}
              />
            )}
          </div>
        </div>

        {/* Side panel: Link or Tooltip editor */}
        {(linking || editingTooltip) && (
          <div className="w-72 bg-gray-900 border-l border-white/10 p-4 overflow-auto" data-testid="side-panel">
            {linking && (
              <>
                <h4 className="text-white text-sm font-medium mb-1">Link to screen:</h4>
                <p className="text-gray-500 text-xs mb-3">Select which screen this hotspot navigates to</p>
                <div className="space-y-1.5">
                  {otherScreens.map(s => (
                    <button
                      key={s.id}
                      onClick={() => linkHotspot(linking, s.id)}
                      className="w-full text-left bg-white/5 hover:bg-white/10 border border-white/10 rounded p-2 flex items-center gap-2"
                      data-testid={`link-target-${s.id}`}
                    >
                      <img
                        src={`${API}/envivom/media/${s.file_id}`}
                        alt=""
                        className="w-10 h-7 object-cover rounded border border-white/10"
                      />
                      <span className="text-white text-xs truncate">{s.name}</span>
                    </button>
                  ))}
                  {otherScreens.length === 0 && (
                    <p className="text-gray-500 text-xs">No other screens to link to.</p>
                  )}
                </div>
                <button
                  onClick={() => setLinking(null)}
                  className="mt-3 w-full text-gray-400 hover:text-white text-xs py-1.5 border border-white/10 rounded"
                >
                  Cancel
                </button>
              </>
            )}
            {editingTooltip && (
              <>
                <h4 className="text-white text-sm font-medium mb-1">Hotspot Tooltip:</h4>
                <p className="text-gray-500 text-xs mb-3">Text shown when user hovers this hotspot in demo mode</p>
                <textarea
                  value={hotspots.find(h => h.id === editingTooltip)?.tooltip || ''}
                  onChange={(e) => updateTooltip(editingTooltip, e.target.value)}
                  className="w-full bg-white/10 border border-white/20 rounded px-3 py-2 text-sm text-white resize-none"
                  rows={4}
                  placeholder="e.g. Opens temperature settings..."
                  autoFocus
                  data-testid="hotspot-tooltip-input"
                />
                <button
                  onClick={() => setEditingTooltip(null)}
                  className="mt-2 w-full bg-cyan-600 hover:bg-cyan-500 text-white text-xs py-1.5 rounded"
                >
                  Done
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== DEMO MODE ====================

const DemoPlayer = ({ screens, onExit }) => {
  const startScreen = screens.find(s => s.is_start_screen) || screens[0];
  const [currentScreen, setCurrentScreen] = useState(startScreen);
  const [history, setHistory] = useState([]);
  const [hoverHotspot, setHoverHotspot] = useState(null);
  const [speaking, setSpeaking] = useState(false);
  const [showDesc, setShowDesc] = useState(true);
  const synthRef = useRef(window.speechSynthesis);

  const navigateTo = useCallback((screenId) => {
    const target = screens.find(s => s.id === screenId);
    if (target) {
      setHistory(prev => [...prev, currentScreen.id]);
      setCurrentScreen(target);
      setHoverHotspot(null);
      // Stop any ongoing speech
      synthRef.current.cancel();
      setSpeaking(false);
    }
  }, [screens, currentScreen]);

  const goBack = useCallback(() => {
    if (history.length > 0) {
      const prevId = history[history.length - 1];
      setHistory(prev => prev.slice(0, -1));
      const prev = screens.find(s => s.id === prevId);
      if (prev) { setCurrentScreen(prev); setHoverHotspot(null); }
      synthRef.current.cancel();
      setSpeaking(false);
    }
  }, [history, screens]);

  const goHome = useCallback(() => {
    if (startScreen && startScreen.id !== currentScreen?.id) {
      setHistory(prev => [...prev, currentScreen.id]);
      setCurrentScreen(startScreen);
      setHoverHotspot(null);
      synthRef.current.cancel();
      setSpeaking(false);
    }
  }, [startScreen, currentScreen]);

  const handleSpeak = useCallback(() => {
    const text = currentScreen?.description;
    if (!text) return;
    if (speaking) {
      synthRef.current.cancel();
      setSpeaking(false);
      return;
    }
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 0.95;
    // Pick American English female voice
    const voices = synthRef.current.getVoices();
    const femaleUS = voices.find(v => v.lang === 'en-US' && /female|samantha|zira|aria/i.test(v.name))
      || voices.find(v => v.lang === 'en-US' && !/male|david|mark|guy/i.test(v.name))
      || voices.find(v => v.lang.startsWith('en') && /female|samantha/i.test(v.name))
      || voices.find(v => v.lang === 'en-US');
    if (femaleUS) utterance.voice = femaleUS;
    utterance.lang = 'en-US';
    utterance.onend = () => setSpeaking(false);
    utterance.onerror = () => setSpeaking(false);
    synthRef.current.speak(utterance);
    setSpeaking(true);
  }, [currentScreen, speaking]);

  // Preload voices (Chrome loads them async)
  useEffect(() => {
    const synth = synthRef.current;
    synth.getVoices();
    const handleVoices = () => synth.getVoices();
    synth.addEventListener?.('voiceschanged', handleVoices);
    return () => synth.removeEventListener?.('voiceschanged', handleVoices);
  }, []);

  // Cleanup speech on unmount
  useEffect(() => {
    const synth = synthRef.current;
    return () => { synth.cancel(); };
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') { synthRef.current.cancel(); onExit(); }
      if (e.key === 'Backspace') goBack();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onExit, goBack]);

  if (!currentScreen) return <div className="text-white p-8">No screens available.</div>;

  const isVideo = currentScreen.content_type?.startsWith('video') || currentScreen.content_type === 'image/gif';
  const linkedHotspots = (currentScreen.hotspots || []).filter(h => h.target_screen_id);
  const hasDescription = !!currentScreen.description;
  const hoveredHs = linkedHotspots.find(h => h.id === hoverHotspot);

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col" data-testid="demo-player">
      {/* Control bar */}
      <div className="flex items-center justify-between px-4 py-2 bg-gray-900/95 border-b border-white/10 shrink-0">
        <div className="flex items-center gap-2">
          <button
            onClick={() => { synthRef.current.cancel(); onExit(); }}
            className="text-gray-400 hover:text-white text-xs px-2.5 py-1.5 rounded bg-white/5 hover:bg-white/10 flex items-center gap-1.5"
            data-testid="demo-exit-btn"
          >
            <X className="w-3.5 h-3.5" /> Exit
          </button>
          <button
            onClick={goHome}
            disabled={!startScreen || startScreen.id === currentScreen?.id}
            className="text-gray-400 hover:text-white disabled:opacity-30 text-xs px-2.5 py-1.5 rounded bg-white/5 hover:bg-white/10 flex items-center gap-1.5"
            data-testid="demo-home-btn"
          >
            <Home className="w-3.5 h-3.5" /> Home
          </button>
          <button
            onClick={goBack}
            disabled={history.length === 0}
            className="text-gray-400 hover:text-white disabled:opacity-30 text-xs px-2.5 py-1.5 rounded bg-white/5 hover:bg-white/10 flex items-center gap-1.5"
            data-testid="demo-back-btn"
          >
            <ChevronLeft className="w-3.5 h-3.5" /> Back
          </button>
          {hasDescription && (
            <button
              onClick={() => setShowDesc(prev => !prev)}
              className={`text-xs px-2.5 py-1.5 rounded flex items-center gap-1.5 ${showDesc ? 'bg-cyan-600/30 text-cyan-400' : 'bg-white/5 text-gray-400 hover:text-white'}`}
              data-testid="toggle-desc-btn"
            >
              <FileText className="w-3.5 h-3.5" /> Info
            </button>
          )}
        </div>
        <span className="text-white/50 text-xs">{currentScreen.name}</span>
        <span className="text-gray-600 text-[10px]">ESC to exit &middot; Backspace to go back</span>
      </div>

      {/* Screen display + description panel */}
      <div className="flex-1 flex overflow-hidden">
        {/* Main screen area */}
        <div className="flex-1 flex items-center justify-center overflow-hidden relative">
          <div className="relative max-w-full max-h-full">
            {isVideo ? (
              <video
                key={currentScreen.id}
                src={`${API}/envivom/media/${currentScreen.file_id}`}
                className="max-w-full max-h-[calc(100vh-48px)] object-contain"
                autoPlay loop muted playsInline
              />
            ) : (
              <img
                src={`${API}/envivom/media/${currentScreen.file_id}`}
                alt={currentScreen.name}
                className="max-w-full max-h-[calc(100vh-48px)] object-contain"
              />
            )}

            {/* Clickable hotspots */}
            {linkedHotspots.map(hs => (
              <div
                key={hs.id}
                className="absolute cursor-pointer transition-all duration-150"
                style={{
                  left: `${hs.x}%`, top: `${hs.y}%`,
                  width: `${hs.width}%`, height: `${hs.height}%`,
                  background: hoverHotspot === hs.id ? 'rgba(59,130,246,0.15)' : 'transparent',
                  border: hoverHotspot === hs.id ? '1px solid rgba(59,130,246,0.4)' : '1px solid transparent',
                  borderRadius: '4px'
                }}
                onClick={() => navigateTo(hs.target_screen_id)}
                onMouseEnter={() => setHoverHotspot(hs.id)}
                onMouseLeave={() => setHoverHotspot(null)}
                data-testid={`demo-hotspot-${hs.id}`}
              >
                {/* Tooltip on hover */}
                {hoverHotspot === hs.id && hs.tooltip && (
                  <div
                    className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 z-10 pointer-events-none"
                    style={{ minWidth: '120px', maxWidth: '280px' }}
                  >
                    <div className="bg-gray-900/95 border border-cyan-500/30 text-white text-xs px-3 py-2 rounded-lg shadow-lg backdrop-blur-sm">
                      {hs.tooltip}
                    </div>
                    <div className="w-2 h-2 bg-gray-900/95 border-b border-r border-cyan-500/30 rotate-45 absolute left-1/2 -translate-x-1/2 -bottom-1" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Description side panel */}
        {hasDescription && showDesc && (
          <div className="w-80 bg-gray-900/95 border-l border-white/10 flex flex-col shrink-0" data-testid="demo-description-panel">
            <div className="p-4 border-b border-white/10 flex items-center justify-between">
              <h4 className="text-white text-sm font-medium flex items-center gap-2">
                <FileText className="w-4 h-4 text-cyan-400" />
                Screen Info
              </h4>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={handleSpeak}
                  className={`p-1.5 rounded transition-colors ${speaking ? 'bg-cyan-600 text-white' : 'bg-white/5 text-gray-400 hover:text-white hover:bg-white/10'}`}
                  title={speaking ? 'Stop reading' : 'Read aloud'}
                  data-testid="tts-btn"
                >
                  {speaking ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                </button>
                <button
                  onClick={() => setShowDesc(false)}
                  className="p-1.5 bg-white/5 text-gray-400 hover:text-white hover:bg-white/10 rounded"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto p-4">
              <p className="text-gray-300 text-sm leading-relaxed whitespace-pre-wrap">{currentScreen.description}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== MAIN PAGE ====================

const EnvivomDemo = ({ user }) => {
  const navigate = useNavigate();
  const [screens, setScreens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEnvivomAdmin, setIsDemoAdmin] = useState(false);
  const [adminChecked, setAdminChecked] = useState(false);
  const [mode, setMode] = useState('demo');
  const [editingScreen, setEditingScreen] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);
  const importInputRef = useRef(null);

  const fetchScreens = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/envivom/screens`);
      setScreens(res.data);
    } catch (err) {
      console.error('Failed to load screens:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchScreens();
    if (user?.email) {
      axios.get(`${API}/envivom/admins`)
        .then(res => {
          const adminEmails = (res.data || []).map(a => a.email.toLowerCase());
          const isAdmin = adminEmails.includes(user.email.toLowerCase());
          setIsDemoAdmin(isAdmin);
          if (isAdmin) setMode('admin');
        })
        .catch((err) => {
          console.error('Demo admin list fetch failed:', err);
        })
        .finally(() => setAdminChecked(true));
    } else {
      setAdminChecked(true);
    }
  }, [fetchScreens, user]);

  const handleUpload = async (files) => {
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('name', file.name.replace(/\.[^.]+$/, ''));
        await axios.post(`${API}/envivom/screens`, fd);
      }
      toast.success(`${files.length} screen(s) uploaded`);
      fetchScreens();
    } catch (err) {
      toast.error('Upload failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
    }
  };

  const handleImportPptx = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/envivom/import-pptx`, fd);
      toast.success(`Imported ${res.data.imported_count} screens from PPTX`);
      fetchScreens();
    } catch (err) {
      toast.error('Import failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setImporting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this screen and its hotspots?')) return;
    try {
      await axios.delete(`${API}/envivom/screens/${id}`);
      toast.success('Screen deleted');
      fetchScreens();
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  const handleUpdate = async (id, data) => {
    try {
      await axios.put(`${API}/envivom/screens/${id}`, data);
      fetchScreens();
    } catch (err) {
      toast.error('Update failed');
    }
  };

  const handleSetStart = async (id) => {
    try {
      await axios.put(`${API}/envivom/screens/${id}`, { is_start_screen: true });
      toast.success('Start screen updated');
      fetchScreens();
    } catch (err) {
      toast.error('Failed to set start screen');
    }
  };

  const handleReplaceMedia = async (id, file) => {
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.put(`${API}/envivom/screens/${id}/media`, fd);
      toast.success('Media replaced');
      fetchScreens();
    } catch (err) {
      toast.error('Replace failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleSaveHotspots = async (screenId, hotspots) => {
    try {
      await axios.put(`${API}/envivom/screens/${screenId}`, { hotspots });
      toast.success('Hotspots saved');
      setEditingScreen(null);
      fetchScreens();
    } catch (err) {
      toast.error('Failed to save hotspots');
    }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm('Delete ALL screens? This cannot be undone.')) return;
    try {
      for (const s of screens) {
        await axios.delete(`${API}/envivom/screens/${s.id}`);
      }
      toast.success('All screens deleted');
      fetchScreens();
    } catch (err) {
      toast.error('Failed to delete screens');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#1a2332' }}>
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (!isEnvivomAdmin && screens.length > 0) {
    return <DemoPlayer screens={screens} onExit={() => navigate('/')} />;
  }

  if (!isEnvivomAdmin && screens.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#1a2332' }}>
        <div className="text-center">
          <Monitor className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <h3 className="text-white text-lg mb-2">Demo not available yet</h3>
          <p className="text-gray-500 text-sm mb-6">The interactive demo is being set up. Check back soon.</p>
          <button onClick={() => navigate('/')} className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1 mx-auto">
            <ArrowLeft className="w-4 h-4" /> Back to Hub
          </button>
        </div>
      </div>
    );
  }

  if (mode === 'demo' && screens.length > 0) {
    return <DemoPlayer screens={screens} onExit={() => setMode('admin')} />;
  }

  if (editingScreen) {
    return (
      <HotspotEditor
        screen={editingScreen}
        screens={screens}
        onSave={handleSaveHotspots}
        onClose={() => setEditingScreen(null)}
      />
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#1a2332' }} data-testid="envivom-demo-page">
      <header className="border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/')} className="text-gray-400 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-lg font-semibold text-white">enviCoM SF Demo</h1>
              <p className="text-xs text-gray-500">Admin — manage screens and hotspots</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {screens.length > 0 && (
              <button
                onClick={() => setMode('demo')}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2"
                data-testid="start-demo-btn"
              >
                <Play className="w-4 h-4" /> Launch Demo
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <h2 className="text-white font-medium">Screens ({screens.length})</h2>
          </div>
          <div className="flex items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              accept="image/*,video/mp4,video/quicktime,video/webm,.gif"
              onChange={(e) => { handleUpload(Array.from(e.target.files)); e.target.value = ''; }}
            />
            <input
              ref={importInputRef}
              type="file"
              className="hidden"
              accept=".pptx"
              onChange={(e) => { handleImportPptx(e.target.files[0]); e.target.value = ''; }}
            />
            <button
              onClick={() => importInputRef.current?.click()}
              disabled={importing}
              className="bg-violet-600/30 hover:bg-violet-600/50 text-violet-300 px-3 py-2 rounded-lg text-sm flex items-center gap-2 border border-violet-500/30"
              data-testid="import-pptx-btn"
            >
              <Upload className="w-4 h-4" /> {importing ? 'Importing...' : 'Import from PPTX'}
            </button>
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg text-sm flex items-center gap-2"
              data-testid="upload-screen-btn"
            >
              <Plus className="w-4 h-4" /> {uploading ? 'Uploading...' : 'Add Screen'}
            </button>
            {screens.length > 0 && (
              <button
                onClick={handleDeleteAll}
                className="bg-red-600/20 hover:bg-red-600/40 text-red-400 px-3 py-2 rounded-lg text-sm"
                data-testid="delete-all-btn"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {screens.length === 0 ? (
          <div className="text-center py-20 border border-dashed border-white/10 rounded-xl">
            <Monitor className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <h3 className="text-white text-lg mb-2">No screens yet</h3>
            <p className="text-gray-500 text-sm mb-6 max-w-md mx-auto">
              Upload controller screenshots, GIFs, or videos to build your interactive demo.
              You can also import all images from a PPTX file at once.
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => importInputRef.current?.click()}
                className="bg-violet-600 hover:bg-violet-700 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2"
              >
                <Upload className="w-4 h-4" /> Import from PPTX
              </button>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2"
              >
                <Plus className="w-4 h-4" /> Upload Files
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {screens.map(screen => (
              <ScreenCard
                key={screen.id}
                screen={screen}
                screens={screens}
                onDelete={handleDelete}
                onUpdate={handleUpdate}
                onSetStart={handleSetStart}
                onEditHotspots={(s) => setEditingScreen(s)}
                onReplaceMedia={handleReplaceMedia}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default EnvivomDemo;
